/**
 * DocsInlineList — inline list of documentation pages linked to an element.
 * Used inside the ServiceForm "Documentación" tab.
 *
 * Relies on DocPageLink (element_type/element_id) which already supports
 * element_type="service".
 */
import React, { useEffect, useState, useCallback } from 'react';
import { ArrowTopRightOnSquareIcon, DocumentTextIcon, LinkIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

const TYPE_ICON = {
  richtext: '📝',
  file:     '📎',
  url:      '🔗',
};

const STATUS_COLORS = {
  draft:     { bg: 'var(--amber-50)', color: 'var(--amber-600)' },
  published: { bg: 'var(--green-50)', color: 'var(--green-600)' },
};

export default function DocsInlineList({ elementType, elementId, elementName, companyId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!elementType || !elementId || !companyId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.getDocPagesForElement(elementType, elementId, companyId);
      setDocs(res.data || []);
    } catch (e) {
      setError(e.message);
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [elementType, elementId, companyId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div style={{ padding: 12, color: 'var(--text-muted)', fontSize: '0.85rem' }}>Cargando documentación…</div>;
  }

  if (error) {
    return <div className="alert alert-danger py-2" style={{ fontSize: '0.82rem' }}>⚠ {error}</div>;
  }

  if (docs.length === 0) {
    return (
      <div className="dv-empty-state">
        <span style={{ fontSize: '1.5rem' }}>📄</span>
        <p style={{ marginTop: 8 }}>Sin documentación vinculada</p>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
          Usa el botón 📄 de la barra superior para buscar o crear documentos.
        </p>
      </div>
    );
  }

  return (
    <div>
      {docs.map(doc => {
        const icon = TYPE_ICON[doc.doc_type] || '📄';
        const statusStyle = STATUS_COLORS[doc.status] || STATUS_COLORS.draft;
        const url = doc.doc_type === 'url' ? doc.external_url
                  : doc.doc_type === 'file' ? (doc.file_path ? `/api/files/${doc.id}` : null)
                  : `/docs/${doc.id}`;

        return (
          <div
            key={doc.id}
            style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 12px', borderBottom: '1px solid var(--border)',
              fontSize: '0.85rem',
            }}
          >
            <span style={{ fontSize: '1.1rem' }}>{icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{doc.title}</div>
              {doc.description && (
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                  {doc.description}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 4, alignItems: 'center' }}>
                <span style={{
                  display: 'inline-block', padding: '1px 6px', borderRadius: 8,
                  fontSize: '0.7rem', fontWeight: 600,
                  background: statusStyle.bg, color: statusStyle.color,
                }}>
                  {doc.status === 'published' ? 'Publicado' : 'Borrador'}
                </span>
                <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                  v{doc.latest_version || 1}
                </span>
                <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                  {doc.doc_type === 'richtext' ? 'Documento' : doc.doc_type === 'file' ? 'Archivo' : 'Enlace'}
                </span>
              </div>
            </div>
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--accent)', textDecoration: 'none' }}
                onClick={e => e.stopPropagation()}
                title="Abrir"
              >
                <ArrowTopRightOnSquareIcon style={{ width: 16, height: 16 }} />
              </a>
            )}
          </div>
        );
      })}
      <div style={{ padding: '8px 12px', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
        {docs.length} documento{docs.length !== 1 ? 's' : ''} vinculado{docs.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}
